import { ServiceError } from '../errors/service-error'
import type { InspectionType } from '../types/inspection.types'

/** Statuses that can participate in the handover paths owned by an inspection. */
export type InspectionWorkflowStatus =
  | 'confirmed'
  | 'pending_pickup'
  | 'active'
  | 'pending_return'
  | 'completed'

export type InspectionStatusHop = {
  from: readonly InspectionWorkflowStatus[]
  to: InspectionWorkflowStatus
}

export type InspectionStatusGateway = {
  /** Move only when the row still has one of the expected source statuses. */
  updateIfCurrent: (input: {
    rentalId: string
    from: readonly InspectionWorkflowStatus[]
    to: InspectionWorkflowStatus
  }) => Promise<boolean>
  /** Read the winner when a guarded update changed zero rows. */
  readCurrent: (rentalId: string) => Promise<string | null>
}

export function inspectionStatusHops(type: InspectionType): readonly InspectionStatusHop[] {
  return type === 'checkin'
    ? [
        { from: ['confirmed'], to: 'pending_pickup' },
        { from: ['pending_pickup'], to: 'active' },
      ]
    : [
        { from: ['active'], to: 'pending_return' },
        { from: ['pending_return'], to: 'completed' },
      ]
}

/**
 * Advance a rental through every legal inspection state, rather than jumping
 * across database-enforced transitions. A zero-row guarded update has two
 * materially different meanings: another attempt already made the same move
 * (safe to resume), or the rental is now somewhere incompatible (must stop).
 */
export async function advanceInspectionRentalStatus({
  rentalId,
  inspectionId,
  type,
  gateway,
  onUnexpectedStatus,
}: {
  rentalId: string
  inspectionId: string
  type: InspectionType
  gateway: InspectionStatusGateway
  onUnexpectedStatus?: (error: ServiceError, hop: InspectionStatusHop) => void
}): Promise<void> {
  const hops = inspectionStatusHops(type)
  const finalStatus = hops[hops.length - 1]?.to

  for (const hop of hops) {
    const changed = await gateway.updateIfCurrent({ rentalId, from: hop.from, to: hop.to })
    if (changed) continue

    const actual = await gateway.readCurrent(rentalId)

    // A retry may resume after another attempt committed this exact hop.
    if (actual === hop.to) continue

    // Or it may arrive after the other attempt completed the whole operation.
    if (actual === finalStatus) break

    const error = new ServiceError({
      code: 'RENTAL_STATUS_UNCHANGED',
      message: `Rental ${rentalId} is '${actual ?? 'unknown'}', expected one of ${hop.from.join(
        ', ',
      )} to move to '${hop.to}'`,
      statusCode: 409,
      details: { rentalId, inspectionId, expected: [...hop.from], actual },
    })
    onUnexpectedStatus?.(error, hop)
    throw error
  }
}
